import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Icon, Menu, T, Tooltip } from 'govwell-ui'
import { IconKey } from 'govwell-ui/components/Icon/util'
import { getLinkProps, isInternalLink, LinkActionProps } from 'govwell-ui/components/Link/util'
import { isNil } from 'lodash'

import { Link } from '@redwoodjs/router'

import { Badge } from 'src/components/shared/Badge'
import { useSidebarContext } from 'src/components/Sidebar/SidebarPrimitive'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMergeRefs } from 'src/hooks/use-merge-refs'
import { KeyboardKey } from 'src/types'
import { LayoutMode } from 'src/utils/layout'

// Find all scrollable parents
const getScrollableParents = (element: HTMLElement): HTMLElement[] => {
  const parents: HTMLElement[] = []
  let parent = element.parentElement

  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent)
    const isScrollable = [style.overflow, style.overflowY, style.overflowX].some(
      (value) => value === 'auto' || value === 'scroll'
    )

    if (isScrollable) {
      parents.push(parent)
    }
    parent = parent.parentElement
  }

  return parents
}

export type NavElement = NavGroup | NavItem
interface INavItem {
  badge?: number
  ref?: React.RefObject<HTMLElement>
  title: string
}
export type NavGroup = INavItem & {
  icon: IconKey
  items: NavItem[]
  key: string
}
export enum NavGroupKey {
  AIAssistant = 'AIAssistant',
  CodeEnforcement = 'CodeEnforcement',
  Companies = 'Companies',
  Insights = 'Insights',
  Locations = 'Locations',
  Payments = 'Payments',
  RecordType = 'RecordType',
  StaffAIAssistant = 'StaffAIAssistant',
}

export type NavItem = INavItem &
  LinkActionProps & {
    isActive: boolean
    isHighlighted?: boolean
  }

type Props = {
  elements: NavElement[]
  isNavGroupOpenMap?: Map<string, boolean>
  onToggleIsOpen?: (key: string) => void
}
const Navigation = ({ elements, isNavGroupOpenMap, onToggleIsOpen }: Props) => {
  const { layoutMode } = useSidebarContext()
  const isCollapsed = layoutMode === LayoutMode.SidebarCollapsed
  return elements.map((element) => {
    if ('items' in element) {
      const { key, ...rest } = element
      return (
        <NavGroup
          key={key}
          groupKey={key}
          isCollapsed={isCollapsed}
          isOpen={isNavGroupOpenMap?.get(key) ?? false}
          onToggleIsOpen={onToggleIsOpen}
          {...rest}
        />
      )
    }

    return (
      <Tooltip key={element.title} content={isCollapsed ? element.title : undefined} side="right">
        <div className="relative w-full min-w-fit">
          <NavItemInternal key={element.title} isCollapsed={isCollapsed} {...element} />
        </div>
      </Tooltip>
    )
  })
}

type NavGroupProps = NavGroup & {
  groupKey: string
  isCollapsed: boolean
  isOpen: boolean
  onToggleIsOpen?: (key: string) => void
}
const NavGroup = forwardRef<HTMLElement, NavGroupProps>(
  (
    { badge, groupKey, icon, isCollapsed, isOpen, items, onToggleIsOpen, title }: NavGroupProps,
    ref
  ) => {
    const internalRef = useRef<HTMLElement>(null)
    const mergedRef = useMergeRefs(internalRef, ...(ref ? [ref] : []))
    const handleClick = useCallback(
      (e?: React.MouseEvent | React.KeyboardEvent) => {
        e?.preventDefault()
        onToggleIsOpen?.(groupKey)
      },
      [groupKey, onToggleIsOpen]
    )

    const { isOpen: isMenuOpen, close: closeMenu, open: openMenu } = useDisclosure()

    const renderedItem = useMemo(
      () => (
        <NavItemInternal
          badge={isOpen ? undefined : badge}
          icon={icon}
          isCollapsed={isCollapsed}
          isHeader
          title={title}
          ref={mergedRef}
          onClick={handleClick}
        />
      ),
      [badge, handleClick, icon, isCollapsed, isOpen, mergedRef, title]
    )

    const handleMouseEnter = useCallback(() => {
      if (isCollapsed) {
        openMenu()
      }
    }, [isCollapsed, openMenu])

    const menuTriggerRef = useRef<HTMLDivElement>(null)
    const menuContentRef = useRef<HTMLDivElement>(null)
    const floatingBarRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      // Forward scroll events from the floating bar to the nav
      const handleFloatingBarWheel = (e: WheelEvent) => {
        e.stopPropagation()
        e.preventDefault()
        if (!menuTriggerRef.current) {
          return
        }
        const scrollableParents = getScrollableParents(menuTriggerRef.current)
        if (scrollableParents[0]) {
          scrollableParents[0].scrollTop += e.deltaY
        }
      }

      if (!isMenuOpen) {
        return
      }

      const frame = requestAnimationFrame(() => {
        // Need to wait a tick because the floating bar mounts one tick after the menu is opened
        const floatingBar = floatingBarRef.current
        if (floatingBar) {
          document.addEventListener('wheel', handleFloatingBarWheel)
        }
      })
      return () => {
        cancelAnimationFrame(frame)
        document.removeEventListener('wheel', handleFloatingBarWheel)
      }
    }, [isMenuOpen])

    useEffect(() => {
      // Guarantee that the menu closes when the cursor exits the menu or floating bar or trigger boundaries
      if (!isCollapsed) {
        return
      }

      let lastMouseX = 0
      let lastMouseY = 0

      const closeMenuIfMouseOutside = () => {
        const isCursorInsideRect = (rect: DOMRect) => {
          return (
            lastMouseX >= rect.left &&
            lastMouseX <= rect.right &&
            lastMouseY >= rect.top &&
            lastMouseY <= rect.bottom
          )
        }

        const menuTriggerRect = menuTriggerRef.current?.getBoundingClientRect()
        const floatingBarRect = floatingBarRef.current?.getBoundingClientRect()
        const menuContentRect = menuContentRef.current?.getBoundingClientRect()

        const isCursorInsideMenu =
          (menuTriggerRect && isCursorInsideRect(menuTriggerRect)) ||
          (floatingBarRect && isCursorInsideRect(floatingBarRect)) ||
          (menuContentRect && isCursorInsideRect(menuContentRect))

        if (!isCursorInsideMenu) {
          closeMenu()
        }
      }

      const handleMenuTriggerMouseOver = (e: MouseEvent) => {
        lastMouseX = e.clientX
        lastMouseY = e.clientY
      }

      const handleWindowMouseMove = (e: MouseEvent) => {
        lastMouseX = e.clientX
        lastMouseY = e.clientY

        closeMenuIfMouseOutside()
      }

      // Handle cases where mouse leaves the entire document
      const handleDocumentMouseLeave = () => {
        closeMenu()
      }

      const handleScroll = () => {
        closeMenuIfMouseOutside()
      }

      const menuTrigger = menuTriggerRef.current
      let scrollableParents: HTMLElement[] = []
      if (menuTrigger) {
        menuTrigger.addEventListener('mouseover', handleMenuTriggerMouseOver)

        scrollableParents = getScrollableParents(menuTrigger)
        scrollableParents.forEach((parent) => {
          parent.addEventListener('scroll', handleScroll)
        })
      }
      window.addEventListener('mousemove', handleWindowMouseMove)
      document.addEventListener('mouseleave', handleDocumentMouseLeave)

      return () => {
        if (menuTrigger) {
          menuTrigger.removeEventListener('mouseover', handleMenuTriggerMouseOver)
        }
        scrollableParents.forEach((parent) => parent.removeEventListener('scroll', handleScroll))
        document.removeEventListener('mouseleave', handleDocumentMouseLeave)
        window.removeEventListener('mousemove', handleWindowMouseMove)
      }
    }, [closeMenu, isCollapsed, openMenu])

    // When a collapsed nav group is hovered, a bar containing the icon shows up to visually connect to the menu, which is just a rectangle
    const [floatingBarTopPx, setFloatingBarTopPx] = useState<number>()
    const [floatingBarLeftPx, setFloatingBarLeftPx] = useState<number>()

    // Calculate how the floating bar should be positioned to conceal the underlying nav icon
    const updateFloatingBarTopPx = useCallback(() => {
      if (!menuTriggerRef.current || !menuContentRef.current) {
        return
      }
      setFloatingBarTopPx(
        menuTriggerRef.current.getBoundingClientRect().top -
          menuContentRef.current.getBoundingClientRect().top
      )
      setFloatingBarLeftPx(
        menuTriggerRef.current.getBoundingClientRect().left -
          menuContentRef.current.getBoundingClientRect().left -
          2
      )
    }, [menuTriggerRef, menuContentRef])

    // When the screen resizes, recalculate the floating bar position
    useEffect(() => {
      const resizeObserver = new ResizeObserver(() => setTimeout(updateFloatingBarTopPx, 500))
      const body = document.documentElement
      if (body) {
        resizeObserver.observe(body)
      }
      return () => {
        resizeObserver.disconnect()
      }
    }, [updateFloatingBarTopPx])

    // When the menu is opened, recalculate the floating bar position
    useEffect(() => {
      if (!isMenuOpen) {
        setFloatingBarTopPx(undefined)
        return
      }
      requestAnimationFrame(() => {
        updateFloatingBarTopPx()
      })
    }, [isMenuOpen, updateFloatingBarTopPx])

    // When the nav group is expanded, close any open menus
    useEffect(() => {
      if (!isCollapsed) {
        closeMenu()
      }
    }, [isCollapsed, closeMenu])

    return (
      <Menu isOpen={isMenuOpen} onClose={closeMenu} onOpen={openMenu} isModal={false}>
        <Menu.Trigger isDisabled={!isCollapsed}>
          <div
            className={`relative w-full ${isCollapsed ? 'max-h-[32px] min-h-[32px] max-w-[32px] min-w-[32px]' : ''}`}
            ref={menuTriggerRef}
            onMouseEnter={handleMouseEnter}
          >
            <Tooltip
              content={!isMenuOpen && isCollapsed ? title : undefined}
              trigger="focus"
              side="right"
            >
              <details
                open={isOpen && !isCollapsed}
                className="[&:not([open])_.nav-group-header-collapse-icon]:hidden [&[open]_.nav-group-header-expand-icon]:hidden"
              >
                <summary
                  className={`mb-[1px] cursor-pointer list-none rounded-lg focus-visible:outline-1 focus-visible:outline-neutral-950 dark:focus-visible:outline-neutral-50 [&::-webkit-details-marker]:hidden ${isCollapsed ? '' : 'hover:bg-neutral-150 dark:hover:bg-neutral-600'}`}
                  onClick={handleClick}
                >
                  {renderedItem}
                </summary>
                {isOpen && (
                  <ol className="ml-4 flex flex-col gap-0.5 border-l-1 border-l-neutral-200 p-0 dark:border-l-neutral-400">
                    {items.map((item) => (
                      <li key={item.title}>
                        <NavItemInternal {...item} isSubItem />
                      </li>
                    ))}
                  </ol>
                )}
              </details>
            </Tooltip>
          </div>
        </Menu.Trigger>
        <Menu.Content
          side="right"
          className="overflow-visible filter-[drop-shadow(0px_0px_2px_gray)] dark:bg-neutral-800"
          sideOffset={6}
          ref={menuContentRef}
        >
          <div
            className={`fixed -z-1 h-[32px] w-[58px] rounded-md bg-white dark:bg-neutral-800 ${isNil(floatingBarTopPx) ? 'invisible' : ''}`}
            ref={floatingBarRef}
            style={{
              top: floatingBarTopPx,
              left: floatingBarLeftPx,
            }}
          >
            <Icon icon={icon} className="h-[20px] w-[20px] p-2 dark:text-neutral-100" />
          </div>
          <Menu.Label>
            <T.Body className="px-2 py-1 font-semibold dark:text-neutral-100">{title}</T.Body>
          </Menu.Label>
          {items.map((item) => (
            <Menu.Item
              key={item.title}
              className="dark:text-neutral-100 dark:hover:bg-neutral-600 dark:focus:bg-neutral-600 dark:focus:text-neutral-100"
              {...(isInternalLink(item)
                ? { internalRoute: item.internalRoute }
                : item.externalHref
                  ? { externalHref: item.externalHref }
                  : { onClick: item.onClick ?? (() => {}) })}
            >
              {item.title} {item.badge && <Badge count={item.badge} />}
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu>
    )
  }
)

type NavItemInternalProps = {
  isActive?: boolean
  isCollapsed?: boolean
  isHeader?: boolean
  isSubItem?: boolean
  isHighlighted?: boolean
  icon?: IconKey
  title: string
  badge?: number
} & LinkActionProps
const NavItemInternal = forwardRef<HTMLElement, NavItemInternalProps>(
  (props: NavItemInternalProps, ref) => {
    const {
      externalHref,
      icon,
      isActive = false,
      isCollapsed = false,
      isHeader = false,
      isSubItem = false,
      isHighlighted = false,
      onClick,
      title,
      badge,
    } = props
    const mergedRef = useMergeRefs<HTMLElement>(ref)
    const content = useMemo(
      () => (
        <>
          {icon && (
            <Icon
              className={`h-[20px] w-[20px] ${
                isHighlighted
                  ? 'text-orange-500 group-hover:!text-neutral-50 dark:group-hover:!text-neutral-950'
                  : isActive
                    ? 'text-neutral-900 dark:text-neutral-100'
                    : 'text-neutral-700 dark:text-neutral-300'
              } `}
              icon={icon}
            />
          )}
          {!isCollapsed && (
            <>
              <T.Body
                as="span"
                className={`text-left font-medium ${
                  isHighlighted
                    ? 'text-orange-500 group-hover:!text-neutral-50 dark:group-hover:!text-neutral-950'
                    : 'dark:text-neutral-50'
                } ${isHeader ? 'flex-1' : ''}`}
              >
                {title}
              </T.Body>
              {!!badge && <Badge count={badge} />}
              {isHeader && (
                <>
                  <T.Body className="nav-group-header-expand-icon text-neutral-950 dark:text-neutral-50">
                    <Icon.FaChevronDown />
                  </T.Body>
                  <T.Body className="nav-group-header-collapse-icon text-neutral-950 dark:text-neutral-50">
                    <Icon.FaChevronUp />
                  </T.Body>
                </>
              )}
            </>
          )}
        </>
      ),
      [isActive, icon, isCollapsed, title, badge, isHeader, isHighlighted]
    )

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === KeyboardKey.Enter || e.key === KeyboardKey.Space) {
          void onClick?.(e)
        }
      },
      [onClick]
    )

    const classNames = `
    relative
    box-border
    flex
    items-center
    gap-3
    px-1.5
    py-2
    w-full
    select-none
    no-underline
    cursor-pointer
    text-neutral-950
    dark:text-neutral-50
    border-none
    ${isHighlighted ? 'group' : ''}
    ${isHighlighted ? '' : 'hover:bg-neutral-150 dark:hover:bg-neutral-600'}
    focus-visible:!outline-1
    focus-visible:!outline-neutral-950
    dark:focus-visible:!outline-neutral-50
    ${isSubItem ? 'rounded-r-lg pl-[21px]' : 'rounded-lg'}
    ${
      isActive
        ? `
      bg-neutral-150
      dark:bg-neutral-600
      ${isSubItem ? 'border-l before:content-[""] before:absolute before:inset-[0px] before:-left-px before:border-l before:border-orange-500' : ''}
    `
        : 'bg-transparent'
    }
    ${
      isHighlighted
        ? 'border-l-2 border-orange-500 bg-orange-50/50 dark:bg-orange-950/30 hover:bg-orange-500 hover:text-neutral-50 dark:hover:bg-orange-500 dark:hover:text-neutral-950'
        : ''
    }
    ${isHeader ? 'pointer-events-none' : ''}
    ${isCollapsed ? 'max-w-[32px] min-w-[32px]' : ''}
  `

    if (onClick) {
      return (
        <button
          className={classNames}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          ref={mergedRef}
          tabIndex={isHeader ? -1 : 0}
        >
          {content}
        </button>
      )
    }
    const { rel, target } = getLinkProps(!!externalHref)

    if (isInternalLink(props)) {
      return (
        <Link
          className={classNames}
          ref={mergedRef}
          rel={rel}
          tabIndex={isHeader ? -1 : 0}
          target={target}
          to={props.internalRoute}
        >
          {content}
        </Link>
      )
    } else {
      return (
        <a
          className={classNames}
          href={externalHref}
          ref={mergedRef}
          rel={rel}
          tabIndex={isHeader ? -1 : 0}
          target={target}
        >
          {content} <Icon.FaArrowUpRightFromSquare className="text-sm" />
        </a>
      )
    }
  }
)

export default React.memo(Navigation)
